/**
 * StatcastChartRenderer - Renders charts based on chart specs
 *
 * ADK Multi-Agent: This component renders data visualizations
 * based on chart specifications generated by the StatCast agent.
 *
 * Supported chart types:
 * - line: Time series data (velocity trends, etc.)
 * - bar: Categorical data (pitch arsenal, distribution)
 * - histogram: Distribution data (velocity bins)
 * - scatter: Correlation data (velocity vs spin)
 * - heatmap: Strike zone pitch location
 *
 * Features:
 * - Enhanced tooltips with MLB context and benchmarks
 * - Export to PNG/CSV functionality
 * - Share link generation
 */

import React, { useMemo, useRef, useCallback, useState } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Download, Share2, Check, Image } from 'lucide-react';

// Color palette for charts
const CHART_COLORS = [
  '#6366f1', // indigo
  '#22c55e', // green
  '#f59e0b', // amber
  '#ef4444', // red
  '#8b5cf6', // violet
  '#06b6d4', // cyan
  '#f97316', // orange
  '#ec4899', // pink
];

// Pitch type colors (matching Statcast conventions)
const PITCH_COLORS = {
  FF: '#ef4444', // 4-seam - red
  SI: '#f97316', // Sinker - orange
  FC: '#8b5cf6', // Cutter - violet
  SL: '#eab308', // Slider - yellow
  CU: '#22c55e', // Curveball - green
  CH: '#3b82f6', // Changeup - blue
  FS: '#6366f1', // Splitter - indigo
  ST: '#14b8a6', // Sweeper - teal
  KC: '#10b981', // Knuckle curve - emerald
  KN: '#6b7280', // Knuckleball - gray
};

// MLB Benchmark Data (2024 averages)
const MLB_BENCHMARKS = {
  release_speed: {
    label: 'Velocity',
    unit: 'mph',
    avg: 93.5,
    elite: 96.0,
    description: 'Average fastball velocity across MLB',
    pitchTypeAvg: {
      FF: 94.5,
      SI: 93.5,
      FC: 88.5,
      SL: 85.0,
      CU: 79.5,
      CH: 85.5,
      FS: 84.0,
      ST: 82.0,
    },
  },
  release_spin_rate: {
    label: 'Spin Rate',
    unit: 'rpm',
    avg: 2300,
    elite: 2600,
    description: 'Average spin rate for fastballs',
    pitchTypeAvg: {
      FF: 2350,
      SI: 2150,
      FC: 2400,
      SL: 2500,
      CU: 2750,
      CH: 1750,
      FS: 1450,
      ST: 2650,
    },
  },
  pfx_x: {
    label: 'Horizontal Break',
    unit: 'in',
    avg: 6.5,
    description: 'Average horizontal movement (arm-side positive)',
  },
  pfx_z: {
    label: 'Vertical Break',
    unit: 'in',
    avg: 14.5,
    description: 'Average induced vertical break',
  },
  launch_speed: {
    label: 'Exit Velocity',
    unit: 'mph',
    avg: 88.5,
    elite: 95.0,
    description: 'Average exit velocity on batted balls',
  },
  launch_angle: {
    label: 'Launch Angle',
    unit: '°',
    avg: 12.0,
    sweetSpot: { min: 8, max: 32 },
    description: 'Optimal "sweet spot" is 8-32 degrees',
  },
  release_extension: {
    label: 'Extension',
    unit: 'ft',
    avg: 6.2,
    elite: 7.0,
    description: 'Distance toward home plate at release',
  },
  whiff_pct: {
    label: 'Whiff Rate',
    unit: '%',
    avg: 24.5,
    elite: 35.0,
    description: 'Percentage of swings that miss',
  },
};

/**
 * Get context text for a metric value compared to MLB average
 */
function getMetricContext(field, value, pitchType = null) {
  const benchmark = MLB_BENCHMARKS[field];
  if (!benchmark || value === null || value === undefined) return null;

  // Get the appropriate average (pitch-type specific if available)
  const avg = pitchType && benchmark.pitchTypeAvg?.[pitchType]
    ? benchmark.pitchTypeAvg[pitchType]
    : benchmark.avg;

  if (!avg) return null;

  const diff = value - avg;
  const pctDiff = Math.abs((diff / avg) * 100).toFixed(1);

  let context = '';
  let rating = '';

  if (Math.abs(diff) < avg * 0.02) {
    rating = 'average';
    context = `MLB avg: ${avg}${benchmark.unit}`;
  } else if (diff > 0) {
    rating = diff > avg * 0.05 ? 'elite' : 'above-avg';
    context = `+${pctDiff}% vs MLB avg (${avg}${benchmark.unit})`;
  } else {
    rating = Math.abs(diff) > avg * 0.05 ? 'below-avg' : 'slightly-below';
    context = `${pctDiff}% below MLB avg (${avg}${benchmark.unit})`;
  }

  // Check elite threshold
  if (benchmark.elite && value >= benchmark.elite) {
    rating = 'elite';
    context = `ELITE: Top tier MLB (>${benchmark.elite}${benchmark.unit})`;
  }

  return { context, rating, description: benchmark.description };
}

/**
 * Enhanced Tooltip Content Component
 */
function EnhancedTooltipContent({ active, payload, label, spec, isDarkMode }) {
  if (!active || !payload || payload.length === 0) return null;

  const bgColor = '#1f2937';
  const borderColor = '#374151';
  const textColor = '#f3f4f6';
  const mutedColor = '#9ca3af';

  return (
    <div
      style={{
        backgroundColor: bgColor,
        border: `1px solid ${borderColor}`,
        borderRadius: '8px',
        padding: '12px',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        maxWidth: '280px',
      }}
    >
      {/* Label */}
      <p style={{ color: textColor, fontWeight: 600, marginBottom: '8px', fontSize: '14px' }}>
        {label}
      </p>

      {/* Values with context */}
      {payload.map((entry, index) => {
        const field = entry.dataKey;
        const value = entry.value;
        const contextInfo = getMetricContext(field, value);

        // Determine rating color
        let ratingColor = mutedColor;
        if (contextInfo?.rating === 'elite') ratingColor = '#22c55e';
        else if (contextInfo?.rating === 'above-avg') ratingColor = '#3b82f6';
        else if (contextInfo?.rating === 'below-avg') ratingColor = '#f59e0b';

        return (
          <div key={index} style={{ marginBottom: index < payload.length - 1 ? '8px' : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <span
                style={{
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  backgroundColor: entry.color || '#6366f1',
                }}
              />
              <span style={{ color: textColor, fontWeight: 500 }}>
                {formatValue(value, field)}
              </span>
              <span style={{ color: mutedColor, fontSize: '12px' }}>
                {entry.name || field.replace(/_/g, ' ')}
              </span>
            </div>

            {/* MLB Context */}
            {contextInfo && (
              <div style={{ marginLeft: '16px', marginTop: '4px' }}>
                <span
                  style={{
                    fontSize: '11px',
                    color: ratingColor,
                    fontWeight: 500,
                  }}
                >
                  {contextInfo.context}
                </span>
                {contextInfo.description && (
                  <p style={{ fontSize: '10px', color: mutedColor, marginTop: '2px' }}>
                    ℹ️ {contextInfo.description}
                  </p>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Format number for display
 */
function formatValue(value, field) {
  if (value === null || value === undefined) return '-';
  if (typeof value !== 'number') return value;

  // Velocity: 1 decimal
  if (/velocity|speed|velo/i.test(field)) {
    return value.toFixed(1);
  }
  // Spin: no decimals
  if (/spin/i.test(field)) {
    return Math.round(value).toLocaleString();
  }
  // Percentages
  if (/pct|percent|rate/i.test(field)) {
    return (value * 100).toFixed(1) + '%';
  }
  // Default: 2 decimals for floats
  if (!Number.isInteger(value)) {
    return value.toFixed(2);
  }
  return value.toLocaleString();
}

/**
 * Chart Export/Share Controls Component
 */
function ChartControls({ chartRef, data, spec, isDarkMode }) {
  const [copied, setCopied] = useState(false);

  const handleExportPNG = useCallback(async () => {
    if (!chartRef?.current) return;

    try {
      // Use html2canvas if available, otherwise fallback to basic method
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const chartElement = chartRef.current;

      // Use the SVG from recharts
      const svg = chartElement.querySelector('svg');
      if (!svg) {
        console.warn('No SVG found in chart');
        return;
      }

      const svgData = new XMLSerializer().serializeToString(svg);
      const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
      const url = URL.createObjectURL(svgBlob);

      // Download SVG (PNG conversion requires more setup)
      const link = document.createElement('a');
      link.href = url;
      link.download = `${spec.title || 'statcast-chart'}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
    }
  }, [chartRef, spec]);

  const handleExportCSV = useCallback(() => {
    if (!data || data.length === 0) return;

    // Build CSV
    const headers = Object.keys(data[0]);
    const csvRows = [
      headers.join(','),
      ...data.map(row =>
        headers.map(h => {
          const val = row[h];
          // Escape commas and quotes in strings
          if (typeof val === 'string' && (val.includes(',') || val.includes('"'))) {
            return `"${val.replace(/"/g, '""')}"`;
          }
          return val ?? '';
        }).join(',')
      )
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${spec.title || 'statcast-data'}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [data, spec]);

  const handleShare = useCallback(async () => {
    // Generate shareable data (could be expanded to create actual share links)
    const shareData = {
      title: spec.title || 'Statcast Analysis',
      text: `${spec.title}: ${data?.length || 0} data points analyzed`,
      // In production, this would generate a real shareable URL
      url: window.location.href,
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (error) {
        // User cancelled or share failed
        console.log('Share cancelled');
      }
    } else {
      // Fallback: copy to clipboard
      const textToCopy = `${shareData.title}\n${shareData.text}\n${shareData.url}`;
      await navigator.clipboard.writeText(textToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [spec, data]);

  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '6px 10px',
    borderRadius: '6px',
    fontSize: '11px',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.2s',
    background: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    color: '#9ca3af',
  };

  return (
    <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
      <button
        onClick={handleExportPNG}
        style={buttonStyle}
        title="Export as SVG"
        onMouseEnter={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.05)';
        }}
      >
        <Image size={14} />
        Export
      </button>
      <button
        onClick={handleExportCSV}
        style={buttonStyle}
        title="Download CSV data"
        onMouseEnter={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.1)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = 'rgba(255, 255, 255, 0.05)';
        }}
      >
        <Download size={14} />
        CSV
      </button>
      <button
        onClick={handleShare}
        style={{
          ...buttonStyle,
          background: copied
            ? ('rgba(34, 197, 94, 0.2)')
            : buttonStyle.background,
          borderColor: copied
            ? ('rgba(34, 197, 94, 0.3)')
            : buttonStyle.borderColor,
          color: copied ? '#22c55e' : buttonStyle.color,
        }}
        title="Share chart"
        onMouseEnter={(e) => {
          if (!copied) {
            e.target.style.background = 'rgba(255, 255, 255, 0.1)';
          }
        }}
        onMouseLeave={(e) => {
          if (!copied) {
            e.target.style.background = 'rgba(255, 255, 255, 0.05)';
          }
        }}
      >
        {copied ? <Check size={14} /> : <Share2 size={14} />}
        {copied ? 'Copied!' : 'Share'}
      </button>
    </div>
  );
}


/**
 * Line Chart for time series data
 */
function LineChartComponent({ data, spec, isDarkMode }) {
  const colors = ['#818cf8', '#34d399', '#fbbf24']; // OLED dark colors

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={'#374151'} />
        <XAxis
          dataKey={spec.x}
          tick={{ fontSize: 12, fill: '#9ca3af' }}
          tickFormatter={(v) => typeof v === 'string' && v.length > 10 ? v.slice(5) : v}
        />
        <YAxis
          tick={{ fontSize: 12, fill: '#9ca3af' }}
          tickFormatter={(v) => formatValue(v, spec.y)}
          label={{
            value: spec.yLabel,
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 12, fill: '#9ca3af' },
          }}
        />
        <Tooltip
          content={<EnhancedTooltipContent spec={spec} isDarkMode={isDarkMode} />}
        />
        <Legend />
        <Line
          type="monotone"
          dataKey={spec.y}
          stroke={colors[0]}
          strokeWidth={2}
          dot={{ fill: colors[0], r: 3 }}
          activeDot={{ r: 5 }}
          name={spec.yLabel}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

/**
 * Bar Chart for categorical data
 */
function BarChartComponent({ data, spec, isDarkMode }) {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={'#374151'} />
        <XAxis
          dataKey={spec.x}
          tick={{ fontSize: 12, fill: '#9ca3af' }}
          angle={-45}
          textAnchor="end"
          height={60}
        />
        <YAxis
          tick={{ fontSize: 12, fill: '#9ca3af' }}
          tickFormatter={(v) => formatValue(v, spec.y)}
        />
        <Tooltip
          content={<EnhancedTooltipContent spec={spec} isDarkMode={isDarkMode} />}
        />
        <Bar dataKey={spec.y} name={spec.yLabel}>
          {data.map((entry, index) => {
            // Use pitch type colors if available
            const pitchType = entry[spec.x];
            const color = PITCH_COLORS[pitchType] || CHART_COLORS[index % CHART_COLORS.length];
            return <Cell key={`cell-${index}`} fill={color} />;
          })}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Scatter Chart for correlation data
 */
function ScatterChartComponent({ data, spec, isDarkMode }) {
  return (
    <ResponsiveContainer width="100%" height={300}>
      <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={'#374151'} />
        <XAxis
          dataKey={spec.x}
          type="number"
          tick={{ fontSize: 12, fill: '#9ca3af' }}
          tickFormatter={(v) => formatValue(v, spec.x)}
          name={spec.xLabel}
          label={{
            value: spec.xLabel,
            position: 'bottom',
            style: { fontSize: 12, fill: '#9ca3af' },
          }}
        />
        <YAxis
          dataKey={spec.y}
          type="number"
          tick={{ fontSize: 12, fill: '#9ca3af' }}
          tickFormatter={(v) => formatValue(v, spec.y)}
          name={spec.yLabel}
          label={{
            value: spec.yLabel,
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 12, fill: '#9ca3af' },
          }}
        />
        <Tooltip
          content={<EnhancedTooltipContent spec={spec} isDarkMode={isDarkMode} />}
        />
        <Scatter
          data={data}
          fill={'#818cf8'}
        />
      </ScatterChart>
    </ResponsiveContainer>
  );
}

/**
 * Simple Strike Zone Heatmap (simplified version)
 * For full heatmap, use the StrikeZoneHeatmap component
 */
function HeatmapPlaceholder({ data, spec, isDarkMode }) {
  // Count pitches in each zone (simplified 3x3 grid)
  const zones = useMemo(() => {
    const grid = Array(9).fill(0);
    data.forEach(pitch => {
      const x = pitch.plate_x;
      const z = pitch.plate_z;
      if (x === undefined || z === undefined) return;

      // Map to 3x3 grid (zones 1-9)
      const col = x < -0.28 ? 0 : x > 0.28 ? 2 : 1;
      const row = z > 2.5 ? 0 : z < 2.0 ? 2 : 1;
      const zoneIdx = row * 3 + col;
      grid[zoneIdx]++;
    });
    return grid;
  }, [data]);

  const maxCount = Math.max(...zones, 1);

  return (
    <div className="flex flex-col items-center p-4">
      <div className="text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
        {spec.title || 'Pitch Location'}
      </div>
      <div className="grid grid-cols-3 gap-1 w-48 h-48 bg-green-900/20 dark:bg-green-900/40 rounded-lg p-2">
        {zones.map((count, idx) => {
          const intensity = count / maxCount;
          const bgColor = intensity > 0.7
            ? 'bg-red-500'
            : intensity > 0.4
              ? 'bg-orange-400'
              : intensity > 0.1
                ? 'bg-yellow-300'
                : 'bg-gray-200 dark:bg-gray-700';

          return (
            <div
              key={idx}
              className={`${bgColor} rounded flex items-center justify-center text-xs font-bold text-gray-900`}
              style={{ opacity: Math.max(0.3, intensity) }}
              title={`Zone ${idx + 1}: ${count} pitches`}
            >
              {count > 0 ? count : ''}
            </div>
          );
        })}
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
        {data.length} total pitches
      </div>
    </div>
  );
}

/**
 * Histogram Chart for distribution data
 * Bins the raw data and displays as a bar chart
 */
function HistogramChartComponent({ data, spec, isDarkMode }) {
  // Bin the data based on the x field (usually velocity)
  const binnedData = useMemo(() => {
    const xField = spec.x;
    const values = data
      .map(d => d[xField])
      .filter(v => v !== null && v !== undefined && typeof v === 'number');

    if (values.length === 0) return [];

    // Calculate bin parameters
    const min = Math.floor(Math.min(...values));
    const max = Math.ceil(Math.max(...values));
    const range = max - min;

    // Determine bin size (aim for 10-20 bins)
    let binSize = Math.ceil(range / 15);
    if (binSize < 1) binSize = 1;

    // Create bins
    const bins = {};
    for (let i = min; i <= max; i += binSize) {
      const binLabel = binSize === 1 ? `${i}` : `${i}-${i + binSize - 1}`;
      bins[binLabel] = { bin: binLabel, count: 0, midpoint: i + binSize / 2 };
    }

    // Count values in each bin
    values.forEach(v => {
      const binIndex = Math.floor((v - min) / binSize);
      const binStart = min + binIndex * binSize;
      const binLabel = binSize === 1 ? `${binStart}` : `${binStart}-${binStart + binSize - 1}`;
      if (bins[binLabel]) {
        bins[binLabel].count++;
      }
    });

    return Object.values(bins).sort((a, b) => a.midpoint - b.midpoint);
  }, [data, spec.x]);

  if (binnedData.length === 0) {
    return (
      <div className="text-center py-8" style={{ color: '#9ca3af' }}>
        No numeric data available for histogram
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={binnedData} margin={{ top: 20, right: 30, left: 20, bottom: 40 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={'#374151'} />
        <XAxis
          dataKey="bin"
          tick={{ fontSize: 11, fill: '#9ca3af' }}
          angle={-45}
          textAnchor="end"
          height={60}
          label={{
            value: spec.xLabel || spec.x,
            position: 'bottom',
            offset: 45,
            style: { fontSize: 12, fill: '#9ca3af' },
          }}
        />
        <YAxis
          tick={{ fontSize: 12, fill: '#9ca3af' }}
          label={{
            value: 'Count',
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 12, fill: '#9ca3af' },
          }}
        />
        <Tooltip
          content={({ active, payload, label }) => {
            if (!active || !payload || payload.length === 0) return null;
            const bgColor = '#1f2937';
            const borderColor = '#374151';
            const textColor = '#f3f4f6';
            const mutedColor = '#9ca3af';

            return (
              <div style={{
                backgroundColor: bgColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '8px',
                padding: '12px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              }}>
                <p style={{ color: textColor, fontWeight: 600, marginBottom: '4px' }}>
                  {spec.xLabel || spec.x}: {label}
                </p>
                <p style={{ color: mutedColor, fontSize: '14px' }}>
                  <span style={{ color: '#818cf8', fontWeight: 600 }}>{payload[0].value}</span> pitches
                </p>
              </div>
            );
          }}
        />
        <Bar dataKey="count" fill={'#818cf8'} name="Pitches" />
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * Statistics Card - Shows key metrics when there's insufficient data for charts
 */
function StatisticsCard({ data, isDarkMode }) {
  if (!data || data.length === 0) return null;

  // Extract numeric fields from data
  const numericFields = {};
  const row = data[0];
  Object.entries(row).forEach(([key, value]) => {
    if (typeof value === 'number') {
      numericFields[key] = value;
    }
  });

  const bgColor = 'rgba(30, 30, 40, 0.8)';
  const borderColor = 'rgba(255, 215, 0, 0.3)';
  const textColor = '#f3f4f6';
  const labelColor = '#9ca3af';
  const accentColor = '#FFD700';

  return (
    <div
      className="rounded-xl p-6"
      style={{
        background: bgColor,
        border: `1px solid ${borderColor}`,
      }}
    >
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Object.entries(numericFields).map(([key, value]) => (
          <div key={key} className="text-center p-3 rounded-lg" style={{ background: 'rgba(0,0,0,0.3)' }}>
            <p className="text-2xl font-bold" style={{ color: accentColor }}>
              {formatValue(value, key)}
            </p>
            <p className="text-xs uppercase tracking-wider mt-1" style={{ color: labelColor }}>
              {key.replace(/_/g, ' ')}
            </p>
          </div>
        ))}
      </div>
      {data.length === 1 && (
        <p className="text-center text-xs mt-4" style={{ color: labelColor }}>
          📊 Single result - showing key metrics
        </p>
      )}
    </div>
  );
}

/**
 * Main Chart Renderer Component
 */
export default function StatcastChartRenderer({ chartSpec, data, isDarkMode = true, showExportControls = true }) {
  const chartRef = useRef(null);

  // Debug logging
  console.log('[StatcastChartRenderer] Props received:', {
    hasSpec: !!chartSpec,
    specType: chartSpec?.type,
    specX: chartSpec?.x,
    specY: chartSpec?.y,
    dataLength: data?.length,
    sampleData: data?.[0] ? Object.keys(data[0]) : [],
  });

  if (!chartSpec || !data || data.length === 0) {
    console.log('[StatcastChartRenderer] Early return - missing data:', { chartSpec: !!chartSpec, data: !!data, dataLength: data?.length });
    return null;
  }

  // Ensure data is an array
  const chartData = Array.isArray(data) ? data : [data];

  // If only 1 data point, show statistics card instead of trying to render a chart
  // Charts need at least 2 points to be meaningful
  if (chartData.length < 2) {
    return (
      <div
        className="w-full rounded-xl shadow-lg p-4"
        style={{
          background: 'rgba(13, 13, 18, 0.8)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        {/* Chart Title */}
        <div className="text-center mb-4">
          <h3
            className="text-lg font-semibold"
            style={{ color: '#f3f4f6' }}
          >
            {chartSpec.title || 'Query Results'}
          </h3>
          <p
            className="text-sm"
            style={{ color: '#9ca3af' }}
          >
            {chartData.length} data point{chartData.length !== 1 ? 's' : ''} - showing metrics view
          </p>
        </div>
        <StatisticsCard data={chartData} isDarkMode={isDarkMode} />
      </div>
    );
  }

  // Dark theme styles
  const containerStyle = {
    background: 'rgba(13, 13, 18, 0.8)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  };

  return (
    <div className="w-full rounded-xl shadow-lg p-4" style={containerStyle}>
      {/* Chart Title */}
      <div className="text-center mb-4">
        <h3
          className="text-lg font-semibold"
          style={{ color: '#f3f4f6' }}
        >
          {chartSpec.title}
        </h3>
        <p
          className="text-sm"
          style={{ color: '#9ca3af' }}
        >
          {chartSpec.dataLength || chartData.length} data points
        </p>
      </div>

      {/* Chart Container with ref for export */}
      <div ref={chartRef}>
        {/* Render appropriate chart type */}
        {chartSpec.type === 'line' && (
          <LineChartComponent data={chartData} spec={chartSpec} isDarkMode={isDarkMode} />
        )}

        {chartSpec.type === 'bar' && (
          <BarChartComponent data={chartData} spec={chartSpec} isDarkMode={isDarkMode} />
        )}

        {chartSpec.type === 'scatter' && (
          <ScatterChartComponent data={chartData} spec={chartSpec} isDarkMode={isDarkMode} />
        )}

        {chartSpec.type === 'heatmap' && (
          <HeatmapPlaceholder data={chartData} spec={chartSpec} isDarkMode={isDarkMode} />
        )}

        {chartSpec.type === 'histogram' && (
          <HistogramChartComponent data={chartData} spec={chartSpec} isDarkMode={isDarkMode} />
        )}

        {/* Fallback for unknown chart types */}
        {!['line', 'bar', 'scatter', 'heatmap', 'histogram'].includes(chartSpec.type) && (
          <div className="text-center py-8" style={{ color: '#9ca3af' }}>
            Chart type "{chartSpec.type}" not yet supported
          </div>
        )}
      </div>

      {/* Export/Share Controls */}
      {showExportControls && (
        <ChartControls
          chartRef={chartRef}
          data={chartData}
          spec={chartSpec}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
}

/**
 * Chart type icon mapping (for trace view)
 */
export function getChartTypeIcon(type) {
  const icons = {
    line: '📈',
    bar: '📊',
    scatter: '⚬',
    heatmap: '🔥',
    histogram: '📶',
  };
  return icons[type] || '📊';
}
